// api/personalData.ts
import apiInstance from "shared/api/axiosConfig";
import { decryptApiBodyToJson, encryptApiBodyFromJSON } from "shared/api/cryptApiBody";
import { PersonalData, PersonalDataType } from "../types/personalData.types";

/**
 * Ajouter plusieurs données personnelles
 * @param personalDatas - Tableau d'objets [{type, value}, ...]
 * @param userType - 'individual' ou 'partner'
 */
export async function addPersonalDatas(personalDatas: PersonalData[], userType: string = 'individual') {
  try {
    const response = await apiInstance.post("/client/personal-data",
      {
        encrypt: encryptApiBodyFromJSON(personalDatas),
        type: userType
      },
    );
    return decryptApiResponse(response.data.encrypt);
  } catch (e) {
    console.error('Erreur addPersonalDatas:', e);
    throw e;
  }
}

/**
 * Récupérer toutes les données personnelles du client
 * @param userType - 'individual' ou 'partner'
 * @param filterType - Filtrer par type (optionnel)
 */
export async function getPersonalDatas(userType: string = 'individual', filterType?: PersonalDataType) {
  try {
    const response = await apiInstance.post("/client/personal-data/list",
      {
        type: userType,
        filterType: filterType
      },
    );
    return decryptApiResponse(response.data.encrypt);
  } catch (e) {
    console.error('Erreur getPersonalDatas:', e);
    throw e;
  }
}

/**
 * Récupérer une donnée personnelle spécifique par son ID
 * @param id - ID de la donnée personnelle
 * @param userType - 'individual' ou 'partner'
 */
export async function getPersonalDataById(id: number, userType: string = 'individual') {
  try {
    const response = await apiInstance.post("/client/personal-data/get-by-id",
      {
        id: id,
        type: userType
      },
    );
    return decryptApiResponse(response.data.encrypt);
  } catch (e) {
    console.error(`Erreur getPersonalDataById ${id}:`, e);
    throw e;
  }
}

/**
 * Mettre à jour une donnée personnelle
 * @param id - ID de la donnée à mettre à jour
 * @param updateData - Nouvelle valeur
 * @param userType - 'individual' ou 'partner'
 */
export async function updatePersonalData(id: number, updateData: any, userType: string = 'individual') {
  try {
    const response = await apiInstance.patch(`/client/personal-data/${id}`,
      {
        encrypt: encryptApiBodyFromJSON({ updateData }),
        type: userType
      },
    );
    return decryptApiResponse(response.data.encrypt);
  } catch (e) {
    console.error(`Erreur updatePersonalData ${id}:`, e);
    throw e;
  }
}

/**
 * Supprimer une donnée personnelle
 * @param id - ID de la donnée à supprimer
 * @param userType - 'individual' ou 'partner'
 */
export async function deletePersonalData(id: number, userType: string = 'individual') {
  try {
    const response = await apiInstance.delete(`/client/personal-data/${id}`, {
      data: { type: userType },
    });
    return response.data;
  } catch (e) {
    console.error(`Erreur deletePersonalData ${id}:`, e);
    throw e;
  }
}

/**
 * Rechercher des données personnelles par type et valeur
 * @param type - Type de donnée
 * @param value - Valeur à rechercher
 * @param userType - 'individual' ou 'partner'
 */
export async function searchPersonalData(type: PersonalDataType, value: string, userType: string = 'individual') {
  try {
    const response = await apiInstance.post("/client/personal-data/search",
      {
        type: type,
        value: value,
        userType: userType
      },
    );
    return decryptApiResponse(response.data.encrypt);
  } catch (e) {
    console.error('Erreur searchPersonalData:', e);
    throw e;
  }
}

/**
 * Récupérer les données personnelles par type spécifique
 * @param userType - 'individual' ou 'partner'
 * @param dataType - Type de données à récupérer
 */
export async function getPersonalDatasByType(userType: string = 'individual', dataType: PersonalDataType) {
  try {
    const response = await apiInstance.post("/client/personal-data/list",
      {
        type: userType,
        filterType: dataType
      },
    );
    return decryptApiResponse(response.data.encrypt);
  } catch (e) {
    console.error(`Erreur getPersonalDatasByType ${dataType}:`, e);
    throw e;
  }
}

/**
 * Vérifier si une donnée personnelle existe déjà
 * @param type - Type de donnée
 * @param value - Valeur à vérifier
 * @param userType - 'individual' ou 'partner'
 */
export async function checkPersonalDataExists(type: PersonalDataType, value: string, userType: string = 'individual') {
  try {
    const results = await searchPersonalData(type, value, userType);
    return results && results.data && results.data.length > 0;
  } catch (e) {
    console.error('Erreur checkPersonalDataExists:', e);
    return false;
  }
}

/**
 * Fonction utilitaire pour déchiffrer la réponse de l'API
 */
export function decryptApiResponse(encryptedData: string): any {
  if (!encryptedData) return null;
  try {
    return decryptApiBodyToJson(encryptedData);
  } catch (e) {
    console.error('Erreur decryptApiResponse:', e);
    return null;
  }
}
